// React island entry point. Bundled by build.mjs into app/static/js/app.min.js,
// which Rhino auto-loads on every page.
//
// Mounting strategy: Shiny renders modules on the client after the WebSocket
// connects, so a slot div may not exist at DOMContentLoaded. We try once
// immediately (covers static slots) and again on shiny:connected (covers
// slots rendered by Shiny). The dataset.mounted flag prevents double-mounts.

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ResultsColumnIsland } from "./components/results_column_island";
import { mountInputIslands } from "./components/inputs/mount";
import { CurrentReform } from "./components/current_reform";
import { initJumpButtons } from "./nav/jump_buttons";
import { initConnectionOverlays } from "./status/connection_overlays";
import {
  initShareRestore,
  setRestoreCompleteHandler,
} from "./state/share_restore";

// Loading / disconnect overlays (#242). Wire first so the shiny:connected and
// shiny:disconnected listeners are attached before Shiny's events fire.
initConnectionOverlays();

// Decode any shared `?s=` reform once, before islands mount — each island pulls
// its restored initial value on mount via state/share_restore. (#244)
initShareRestore();

// Auto-run the shared reform once every restored control has committed (#297).
// The recipient came to see the result, so click Simulate for them instead of
// leaving the page at baseline. Registered before islands mount; it fires when
// the last restored island reports. Clicking the real button reuses the human
// path exactly — same server-side range clamp, same busy spinner.
setRestoreCompleteHandler(() => {
  const button = document.querySelector<HTMLButtonElement>(
    '[data-testid="simulate-button"]',
  );
  button?.click();
});

function mountSlot(id: string, element: JSX.Element): void {
  const slot = document.getElementById(id);
  if (slot && slot.dataset.mounted !== "true") {
    slot.dataset.mounted = "true";
    createRoot(slot).render(<StrictMode>{element}</StrictMode>);
  }
}

function mountIslands(): void {
  mountSlot("results-column-island", <ResultsColumnIsland />);
  // "Your reform" trigger + modal in the results header (#65). Hosts the
  // relocated Copy-share-link control (#244).
  mountSlot("current-reform-island", <CurrentReform />);
  // Input-side React islands: discovered by querySelector on
  // [data-react-input] slots that the Shiny side renders for `groups:`
  // entries needing a React component. Each kind's factory lives in
  // components/inputs/mount.
  mountInputIslands();
  // Vanilla nav affordance (not a React island). Idempotent; bails if
  // the simulation page isn't currently rendered.
  initJumpButtons();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", mountIslands);
} else {
  mountIslands();
}

document.addEventListener("shiny:connected", mountIslands);
